import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { GoogleLogo, AppleLogo, EnvelopeSimple, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, pw);
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const comingSoon = (provider) => toast(t("auth.coming_soon", { provider }));

  return (
    <AuthShell title={t("auth.welcome_back")} subtitle={t("auth.welcome_sub")}>
      <div className="space-y-2">
        <button data-testid="google-login-btn" onClick={googleLogin} className="kinetic-btn w-full border border-[#262626] hover:border-white/40 bg-[#141414] px-4 py-3 flex items-center justify-center gap-3 text-sm font-semibold">
          <GoogleLogo weight="bold" size={18} /> {t("common.continue_with_google")}
        </button>
        <div>
          <button data-testid="apple-login-btn" onClick={() => comingSoon("Apple")} className="kinetic-btn w-full border border-[#262626] hover:border-white/40 bg-[#141414] px-3 py-2.5 flex items-center justify-center gap-2 text-sm font-semibold">
            <AppleLogo weight="fill" size={16} /> {t("common.continue_with_apple") || "Continue with Apple"}
          </button>
        </div>
      </div>
      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#262626]" />
        <span className="label-overline">{t("common.or")}</span>
        <div className="h-px flex-1 bg-[#262626]" />
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field icon={<EnvelopeSimple size={16} weight="duotone" />}>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("common.email")} className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500" />
        </Field>
        <Field icon={<LockKey size={16} weight="duotone" />}>
          <input data-testid="login-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("common.password")} className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500" />
        </Field>
        {err && <div data-testid="login-error" className="text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2">{err}</div>}
        <button data-testid="login-submit" disabled={loading} className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-60 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
          {loading ? t("auth.login_loading") : t("auth.login_action")}
        </button>
        <div className="text-center pt-1">
          <Link
            data-testid="login-forgot-link"
            to="/forgot-password"
            className="text-xs text-zinc-400 hover:text-white uppercase tracking-widest font-semibold"
          >
            {t("auth.forgot_password_link")}
          </Link>
        </div>
      </form>
      <div className="mt-6 text-xs text-zinc-400 text-center">
        {t("auth.new_to_teamfit")} <Link data-testid="login-to-register" to="/register" className="text-[#007AFF] hover:underline">{t("auth.create_account_link")}</Link>
      </div>
    </AuthShell>
  );
}

export function Field({ icon, children }) {
  return (
    <div className="flex items-center gap-2 border border-[#262626] bg-[#0A0A0A] px-3 py-3 focus-within:border-[#007AFF]">
      <span className="text-zinc-400">{icon}</span>
      {children}
    </div>
  );
}

export function AuthShell({ title, subtitle, children }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="hidden lg:flex relative items-end p-12 border-r border-[#262626] overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/44618a01-ff58-445c-83e6-81e80fc4fb8c/images/ee7bdc7d0bd040b0bc916a79cba7e52f8465453e072e077580d1f0bc4cbbdb85.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative">
          <div className="font-display text-6xl leading-none">{t("auth.move_together1")}<br />{t("auth.move_together2")}</div>
          <div className="label-overline mt-4 text-zinc-300">{t("app.tagline")}</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center"><span className="font-display text-sm">TF</span></div>
            <span className="font-display text-xl tracking-tight">TEAMFIT</span>
          </Link>
          <h1 className="font-display text-4xl tracking-tight">{title}</h1>
          <p className="text-sm text-zinc-400 mt-2">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
